import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { normalizeApiError } from '../../api/apiError';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { RequestFormSelectField } from '../../components/RequestFormSelectField';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getEmployeeLeavePolicies,
  setEmployeeLeavePolicies,
} from '../../services/employeeService';
import type {
  EmployeeLeavePolicies,
  LeavePolicyOption,
} from '../../types/employee';
import { runOperation } from '../../utils/operationFeedback';

const NOT_ASSIGNED = '';

type LeaveTypeGroup = {
  leaveTypeId: number;
  leaveTypeName: string;
  policies: LeavePolicyOption[];
};

/** Selected policy id (or NOT_ASSIGNED) per leave type id. */
type Selection = Record<number, string>;

function groupByLeaveType(info: EmployeeLeavePolicies): LeaveTypeGroup[] {
  const groups = new Map<number, LeaveTypeGroup>();
  // Assigned policies come first so a type stays listed even if its policy
  // is no longer assignable.
  for (const policy of [...info.assignedPolicies, ...info.availablePolicies]) {
    const group = groups.get(policy.leaveTypeId) ?? {
      leaveTypeId: policy.leaveTypeId,
      leaveTypeName: policy.leaveTypeName,
      policies: [],
    };
    if (!group.policies.some(item => item.leavePolicyId === policy.leavePolicyId)) {
      group.policies.push(policy);
    }
    groups.set(policy.leaveTypeId, group);
  }
  return [...groups.values()];
}

function selectionFrom(info: EmployeeLeavePolicies): Selection {
  return Object.fromEntries(
    info.assignedPolicies.map(policy => [
      policy.leaveTypeId,
      String(policy.leavePolicyId),
    ]),
  );
}

function policyLabel(policy: LeavePolicyOption): string {
  return policy.isDefault ? `${policy.policyName} (Default)` : policy.policyName;
}

function policySummary(policy: LeavePolicyOption): string {
  const parts: string[] = [];
  if (policy.annualEntitlement !== null) {
    parts.push(`${policy.annualEntitlement} days per year`);
  }
  if (policy.monthlyAccrual) {
    parts.push(`${policy.monthlyAccrual} credited per month`);
  }
  parts.push(
    policy.carryForwardAllowed ? 'Carry forward allowed' : 'No carry forward',
  );
  return parts.join(' · ');
}

/**
 * HR Admin view of the leave policies an employee applies leave against: one
 * policy per leave type. New employees get the organization's default
 * policies automatically; this card changes them.
 */
export function EmployeeLeavePoliciesCard({
  employeeId,
  canManage,
  isSelf,
}: {
  employeeId: number;
  canManage: boolean;
  isSelf: boolean;
}) {
  const palette = useAppColors();
  const [info, setInfo] = useState<EmployeeLeavePolicies | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      sectionTitle: { ...typography.body, color: palette.textPrimary },
      hint: { ...typography.caption, color: palette.textMuted },
      warning: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const applyInfo = useCallback((next: EmployeeLeavePolicies) => {
    setInfo(next);
    setSelection(selectionFrom(next));
  }, []);

  const load = useCallback(async () => {
    if (!canManage) return;
    setLoadError(null);
    try {
      applyInfo(await getEmployeeLeavePolicies(employeeId));
    } catch (caught) {
      setInfo(null);
      setLoadError(
        normalizeApiError(caught).message || 'Unable to load leave policies.',
      );
    }
  }, [applyInfo, canManage, employeeId]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const groups = useMemo(() => (info ? groupByLeaveType(info) : []), [info]);
  const assigned = useMemo(() => (info ? selectionFrom(info) : {}), [info]);
  const changed = groups.some(
    group =>
      (selection[group.leaveTypeId] ?? NOT_ASSIGNED) !==
      (assigned[group.leaveTypeId] ?? NOT_ASSIGNED),
  );
  const removedTypes = groups.filter(
    group =>
      assigned[group.leaveTypeId] &&
      (selection[group.leaveTypeId] ?? NOT_ASSIGNED) === NOT_ASSIGNED,
  );

  async function save() {
    if (saving || !changed) return;
    const leavePolicyIds = Object.values(selection)
      .filter(value => value !== NOT_ASSIGNED)
      .map(Number);
    setSaving(true);
    setSaveError(null);
    try {
      applyInfo(
        await runOperation('update', () =>
          setEmployeeLeavePolicies(employeeId, leavePolicyIds),
        ),
      );
    } catch (caught) {
      setSaveError(
        normalizeApiError(caught).message || 'Unable to save leave policies.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppCard style={styles.card}>
      <Text style={themed.sectionTitle}>Leave policies</Text>
      {!canManage ? (
        <Text style={themed.hint}>
          {isSelf
            ? 'You cannot change your own leave policy.'
            : 'Only HR Admin can change leave policies.'}
        </Text>
      ) : loadError ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            load().catch(() => undefined);
          }}
        >
          <Text style={themed.warning}>{loadError} Tap to retry.</Text>
        </Pressable>
      ) : !info ? (
        <Text style={themed.hint}>Loading leave policies...</Text>
      ) : groups.length === 0 ? (
        <Text style={themed.warning}>
          No leave policies are configured. Create them in Leave configuration.
        </Text>
      ) : (
        <>
          <Text style={themed.hint}>
            The employee applies leave under these policies. Changes apply to
            new leave applications from today.
          </Text>
          {info.defaultPolicies.length === 0 ? (
            <Text style={themed.warning}>
              No default leave policy is configured, so new employees get no
              leave policy automatically. Mark policies as default in Leave
              configuration.
            </Text>
          ) : null}
          {groups.map(group => {
            const value = selection[group.leaveTypeId] ?? NOT_ASSIGNED;
            const selected = group.policies.find(
              policy => String(policy.leavePolicyId) === value,
            );
            return (
              <View key={group.leaveTypeId} style={styles.group}>
                <RequestFormSelectField
                  label={group.leaveTypeName}
                  value={value}
                  options={[
                    { value: NOT_ASSIGNED, label: 'Not assigned' },
                    ...group.policies.map(policy => ({
                      value: String(policy.leavePolicyId),
                      label: policyLabel(policy),
                    })),
                  ]}
                  onChange={next => {
                    setSaveError(null);
                    setSelection(current => ({
                      ...current,
                      [group.leaveTypeId]: next,
                    }));
                  }}
                  editable={!saving}
                />
                {selected ? (
                  <Text style={themed.hint}>{policySummary(selected)}</Text>
                ) : null}
              </View>
            );
          })}
          {removedTypes.length > 0 ? (
            <Text style={themed.hint}>
              The employee will no longer be able to apply for{' '}
              {removedTypes.map(group => group.leaveTypeName).join(', ')}.
              Existing leave requests and history are kept.
            </Text>
          ) : null}
          {saveError ? (
            <Text accessibilityRole="alert" style={themed.warning}>
              {saveError}
            </Text>
          ) : null}
          <AppButton
            title={saving ? 'Saving…' : 'Save leave policies'}
            variant="secondary"
            onPress={save}
            disabled={saving || !changed}
          />
        </>
      )}
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: { gap: spacing.md },
  group: { gap: spacing.xs },
});
